<template>
  <Popup v-model="showPop" position="bottom" @closed="handleClosed">
    <div class="contact-popup pd-30">
      <button type="button" class="contact-popup__close" @click="close" :aria-label="$t('cancel')"><van-icon name="cross" /></button>
      <div class="contact-popup__title">
        {{ mode === 'list' ? $t('contactPop.listTitle') : (editingContact.id ? $t('contactPop.editTitle') : $t('contactPop.title')) }}
      </div>
      <div class="contact-popup__subTitle">
        {{ mode === 'list' ? $tc('contactPop.selectRequired', needCount, { count: needCount }) : $t('contactPop.subTitle') }}
      </div>

      <div v-if="mode === 'list'" class="contact-popup__content">
        <div v-if="!loading && !contactLoadError" class="contact-popup__add" @click="startCreate">
          {{ $t('contactPop.addNewContact') }}
        </div>
        <div v-if="loading" class="contact-popup__state">{{ $t('loading') }}</div>
        <div v-else-if="contactLoadError" class="contact-popup__state contact-popup__state--error">
          <div>{{ $t('systemError') }}</div>
          <div class="btn-primary btn-normal" @click="loadContacts">{{ $t('retry') }}</div>
        </div>
        <div v-else-if="contactList.length === 0" class="contact-popup__state">{{ $t('contactPop.empty') }}</div>
        <div v-for="contact in contactList" v-if="!loading && contactList.length > 0" :key="contact.id" class="contact-card" :class="{ selected: isSelected(contact.id) }" @click="toggleContact(contact)">
          <van-checkbox
            class="contact-card__check"
            :value="isSelected(contact.id)"
            shape="round"
            icon-size="18"
            @click.stop
            @input="setContactChecked(contact, $event)"
          />
          <div class="contact-card__body">
            <div class="contact-card__name">{{ contactNameText(contact) }}</div>
            <div class="contact-card__bottom">
              <div class="contact-card__phone">{{ displayPhone(contact) }}</div>
              <div class="contact-card__actions">
                <button type="button" class="contact-card__action contact-card__action--edit" @click.stop="editContact(contact)">
                  <van-icon name="edit" />
                  <span>{{ $t('edit') }}</span>
                </button>
                <span class="contact-card__action-divider"></span>
                <button type="button" class="contact-card__action contact-card__action--delete" @click.stop="removeContact(contact)">
                  <van-icon name="delete-o" />
                  <span>{{ $t('delete') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!loading && !contactLoadError" class="contact-popup__footer">
          <div class="btn-primary btn-normal" :class="{ 'btn-disabled': !canSubmitSelection }" @click="saveSelection">{{ $t('confirm') }}</div>
        </div>
      </div>

      <div v-else class="contact-popup__content contact-popup__form-content">
        <ContactItem :index="0" :contact="editingContact" @update:contact="updateEditingContact" />
        <div class="contact-popup__footer contact-popup__form-footer">
          <div class="contact-popup__back" @click="backToList">{{ $t('cancel') }}</div>
          <div class="btn-primary btn-normal" :class="{ 'btn-disabled': saving }" @click="saveEditingContact">{{ saving ? $t('loading') : $t('contactPop.submit') }}</div>
        </div>
      </div>
    </div>
  </Popup>
</template>

<script>
import { Icon, Popup, Dialog, Checkbox } from 'vant';
import ContactItem from './ContactItem.vue';
import { LANGUAGE, RELATIONSHIP_TYPE } from '@/common/constants';
import i18n from '@/i18n/index';
import { addContact, deleteContact, queryContactList, updateContact } from '@/api/order';
export default {
  name: 'ContactPopup',
  components: {
    Popup,
    ContactItem,
    'van-checkbox': Checkbox,
    'van-icon': Icon
  },
  props: {
    needCount: {
      type: [Number],
      default: 1
    },
    defaultContact: {
      type: Array,
      default: () => []
    },
    userPhone: {
      type: String,
      default: ''
    },
    managementMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showPop: false,
      mode: 'list',
      contactList: [],
      selectedIds: [],
      editingContact: this.emptyContact(),
      loading: false,
      saving: false,
      contactLoadError: false
    }
  },
  computed: {
    canSubmitSelection() {
      return this.needCount > 0 && this.selectedIds.length === this.needCount;
    }
  },
  methods: {
    emptyContact() {
      return { id: '', name: '', phone: '', relation: null };
    },
    normalizePhone(contact = {}) {
      const phone = String(contact.phone || contact.mobile || '').replace(/\D/g, '');
      return phone.startsWith('62') ? phone.slice(2) : phone.replace(/^0/, '');
    },
    normalizeContact(contact = {}) {
      const relationValue = contact.relation?.value || contact.relationship || '';
      return {
        id: contact.id || contact.contactId || '',
        name: contact.name || '',
        phone: this.normalizePhone(contact),
        mobile: contact.mobile || '',
        relationship: relationValue,
        relation: RELATIONSHIP_TYPE.find(item => item.value === relationValue) || null
      };
    },
    validateLocalPhone(phone = '') {
      const local = this.normalizePhone({ phone });
      return /^8\d{9,11}$/.test(local);
    },
    validatePersonName(name = '') {
      const value = String(name).trim();
      return value.length <= 50 &&
        /\p{L}/u.test(value) &&
        /^[\p{L} .'-]+$/u.test(value) &&
        !/\d/.test(value) &&
        !/\s{2,}/.test(value) &&
        !/[.'-]{2,}/.test(value) &&
        !/^[ .'-]|[ .'-]$/.test(value);
    },
    normalizeId(id) {
      return String(id || '');
    },
    async open(options = {}) {
      if (this.needCount < 1) return;
      this.showPop = true;
      this.mode = options.startCreate || options.editContact ? 'form' : 'list';
      this.contactList = [];
      this.contactLoadError = false;
      this.selectedIds = this.defaultContact.map(item => this.normalizeId(item.id || item.contactId)).filter(Boolean);
      await this.loadContacts();
      if (options.editContact) {
        this.editingContact = this.normalizeContact(options.editContact);
      } else if (options.startCreate) {
        this.editingContact = this.emptyContact();
      }
    },
    async loadContacts() {
      this.loading = true;
      this.contactLoadError = false;
      try {
        const res = await queryContactList();
        if (res?.code !== 'Success') {
          this.contactLoadError = true;
          this.$toast(res?.message || this.$t('systemError'));
          return false;
        }
        const records = res.data?.records || res.data?.list || res.data || [];
        this.contactList = Array.isArray(records) ? records.map(this.normalizeContact) : [];
        this.selectedIds = this.selectedIds.filter(id => this.contactList.some(item => this.normalizeId(item.id) === id));
        return true;
      } catch (e) {
        this.contactLoadError = true;
        this.$toast(this.$t('systemError'));
        return false;
      } finally {
        this.loading = false;
      }
    },
    isSelected(id) {
      return this.selectedIds.includes(this.normalizeId(id));
    },
    toggleContact(contact) {
      if (!contact.id) return;
      const contactId = this.normalizeId(contact.id);
      if (this.isSelected(contactId)) {
        this.selectedIds = this.selectedIds.filter(id => id !== contactId);
        return;
      }
      if (this.selectedIds.length >= this.needCount) {
        this.$toast(this.$t('contactPop.maxCount', { count: this.needCount }));
        return;
      }
      this.selectedIds = [...this.selectedIds, contactId];
    },
    setContactChecked(contact, checked) {
      if (!contact.id) return;
      const contactId = this.normalizeId(contact.id);
      const selected = this.isSelected(contactId);
      if (!checked && selected) {
        this.selectedIds = this.selectedIds.filter(id => id !== contactId);
        return;
      }
      if (checked && !selected) {
        if (this.selectedIds.length >= this.needCount) {
          this.$toast(this.$t('contactPop.maxCount', { count: this.needCount }));
          return;
        }
        this.selectedIds = [...this.selectedIds, contactId];
      }
    },
    relationText(contact) {
      const relation = contact.relation || RELATIONSHIP_TYPE.find(item => item.value === contact.relationship);
      if (!relation) return '';
      if (i18n.locale === LANGUAGE.id) return relation.textId;
      if (i18n.locale === LANGUAGE.ph) return relation.textPh;
      return relation.textEn;
    },
    contactNameText(contact) {
      const relation = this.relationText(contact);
      return relation ? `${contact.name} (${relation})` : contact.name;
    },
    displayPhone(contact) {
      const phone = this.normalizePhone(contact);
      if (!phone) return '';
      const groups = [phone.slice(0, 3)];
      for (let index = 3; index < phone.length; index += 4) {
        groups.push(phone.slice(index, index + 4));
      }
      return `+62 ${groups.filter(Boolean).join(' ')}`;
    },
    startCreate() {
      this.editingContact = this.emptyContact();
      this.mode = 'form';
    },
    editContact(contact) {
      this.editingContact = this.normalizeContact(contact);
      this.mode = 'form';
    },
    backToList() {
      if (this.managementMode) {
        this.close();
        return;
      }
      this.mode = 'list';
    },
    updateEditingContact(data) {
      this.editingContact = { ...this.editingContact, ...data.data };
    },
    checkInput(contactList = [this.editingContact]) {
      let validMsg = '';

      // Android 规则：本人号码和联系人库中其他联系人都参与重复校验。
      const currentPhone = this.normalizePhone({ phone: this.$store.state.user.userInfo?.phoneNumber || '' });
      const phones = [];
      if (this.validateLocalPhone(currentPhone)) phones.push(currentPhone);
      this.contactList
        .filter(item => !this.editingContact.id || item.id !== this.editingContact.id)
        .forEach(item => {
          const phone = this.normalizePhone(item);
          if (this.validateLocalPhone(phone)) phones.push(phone);
        });
      for (let i = 0; i < contactList.length; i++) {
        let item = contactList[i];

        // 检查关系是否为空
        if (!item.relation) {
          validMsg = this.$t('contactPop.relationEmpty');
          break;
        }

        // 检查姓名是否为空
        if (!item.name) {
          validMsg = this.$t('contactPop.nameEmpty');
          break;
        }

        if (!this.validatePersonName(item.name)) {
          validMsg = this.$t('contactPop.nameFormat');
          break;
        }

        // 检查电话是否有效
        if (!this.validateLocalPhone(item.phone)) {
          validMsg = this.$t('contactPop.phoneError');
          break;
        }

        // 检查电话是否重复
        const phone = this.normalizePhone(item);
        if (phones.includes(phone)) {
          validMsg = this.$t('contactPop.phoneRepeat');
          break;
        }
        phones.push(phone);
      }

      return validMsg;
    },
    async saveEditingContact() {
      if (this.saving) return;
      const validMsg = this.checkInput();
      if (validMsg) {
        this.$toast(validMsg);
        return;
      }
      this.saving = true;
      const contact = this.editingContact;
      const payload = {
        name: contact.name.trim(),
        mobile: `62${this.normalizePhone(contact)}`,
        relationship: contact.relation?.value || contact.relationship || ''
      };
      try {
        const res = contact.id ? await updateContact(contact.id, payload) : await addContact(payload);
        if (res?.code !== 'Success') {
          this.$toast(res?.message || this.$t('systemError'));
          return;
        }
        const loaded = await this.loadContacts();
        if (!loaded) {
          this.mode = 'list';
          return;
        }
        this.$emit('contacts-changed', { data: this.contactList });
        if (this.managementMode) {
          this.close();
          return;
        }
        const editingId = this.normalizeId(contact.id);
        const saved = this.contactList.find(item => this.normalizeId(item.id) === editingId) || this.contactList[this.contactList.length - 1];
        if (saved?.id && !this.isSelected(saved.id) && this.selectedIds.length < this.needCount) {
          this.selectedIds = [...this.selectedIds, this.normalizeId(saved.id)];
        }
        this.mode = 'list';
      } catch (e) {
        this.$toast(this.$t('systemError'));
      } finally {
        this.saving = false;
      }
    },
    async removeContact(contact) {
      if (!contact.id) return;
      try {
        await Dialog.confirm({
          title: this.$t('contactPop.deleteTitle'),
          message: this.$t('contactPop.deleteConfirm'),
          confirmButtonText: this.$t('confirm'),
          cancelButtonText: this.$t('cancel')
        });
        const res = await deleteContact(contact.id);
        if (res?.code !== 'Success') {
          this.$toast(res?.message || this.$t('systemError'));
          return;
        }
        const deletedId = this.normalizeId(contact.id);
        this.selectedIds = this.selectedIds.filter(id => id !== deletedId);
        await this.loadContacts();
        this.fillSelectionFromContactList();
        this.$emit('selection-change', {
          deletedId,
          data: this.contactList.filter(item => this.selectedIds.includes(this.normalizeId(item.id)))
        });
      } catch (e) {
        // Dialog cancel and network errors are both intentionally non-blocking here.
      }
    },
    saveSelection() {
      if (!this.canSubmitSelection) {
        this.$toast(this.$t('orderPage.contactTip'));
        return;
      }
      this.$emit('success', {
        data: this.contactList.filter(item => this.selectedIds.includes(this.normalizeId(item.id)))
      });
      this.close();
    },
    fillSelectionFromContactList() {
      if (this.needCount < 1 || this.selectedIds.length >= this.needCount) return;
      const nextIds = [...this.selectedIds];
      for (const contact of this.contactList) {
        const contactId = this.normalizeId(contact.id);
        if (!contactId || nextIds.includes(contactId)) continue;
        nextIds.push(contactId);
        if (nextIds.length >= this.needCount) break;
      }
      this.selectedIds = nextIds;
    },
    close() {
      this.showPop = false;
    },
    handleClosed() {
      this.mode = 'list';
      this.editingContact = this.emptyContact();
    }
  }
}
</script>

<style lang="less" scoped>
.van-popup {
  background: none;
}

.sm-radius {
  border-radius: var(--border-radius-base);
}

.contact-popup {
  width: 100%;
  max-width: 750px;
  background: #FFFFFF;
  border-radius: 28px 28px 0 0;
  filter: blur(0px);
  position: relative;

  &__close {
    position: absolute;
    right: 28px;
    top: 34px;
    width: 40px;
    height: 40px;
    border: 0;
    padding: 0;
    background: transparent;
    color: #64748B;
    font-size: 32px;
    line-height: 40px;
  }

  &__title {
    padding-right: 56px;
    color: #0B1744;
    font-size: 36px;
    line-height: 48px;
    font-weight: 400;
  }

  &__subTitle {
    margin-top: 8px;
    padding-bottom: 16px;
    border-bottom: 1px solid #E5E7EB;
    color: #6B7280;
    font-size: 26px;
    line-height: 36px;
  }

  &__content {
    height: auto;
    max-height: 68vh;
    overflow-y: auto;
    --webkit-overflow-scrolling: touch;
    padding-bottom: 140px;
  }

  &__count,
  &__state {
    margin-top: 26px;
    font-size: 26px;
    line-height: 36px;
    color: var(--text-color-gray);
    text-align: center;
  }

  &__add {
    margin-top: 16px;
    height: 80px;
    box-sizing: border-box;
    border: 0;
    border-radius: 14px;
    background: var(--primary-color);
    color: #FFFFFF;
    font-size: 26px;
    line-height: 80px;
    text-align: center;
  }

  &__form-content {
    height: auto;
    max-height: none;
    overflow-y: auto;
    padding-bottom: 0;
    padding-top: 22px;
  }

  &__form-footer {
    position: static;
    display: flex;
    align-items: center;
    gap: 20px;
    margin-top: 24px;
    padding: 0;

    > div {
      flex: 1;
      height: 80px;
      box-sizing: border-box;
      border-radius: 14px;
      line-height: 80px;
    }
  }

  &__back {
    border: 2px solid var(--primary-color);
    background: #FFFFFF;
    color: var(--primary-color);
    font-size: 28px;
    text-align: center;
  }

  &__person {
    margin-top: 40px;
  }

  &__footer {
    position: absolute;
    left: 0;
    bottom: 0;
    padding: 20px 30px;
    width: 100%;
    background: #FFFFFF;

    .btn-normal {
      border-radius: 14px;
    }
  }

  &__footer&__form-footer {
    position: static;
    padding: 0;
    margin-top: 24px;
  }
}

.contact-card {
  display: flex;
  align-items: center;
  margin-top: 24px;
  padding: 28px 24px;
  border: 1px solid #DDE3EE;
  border-radius: 18px;
  background: #FFFFFF;

  &.selected {
    border-color: var(--primary-color);
    background: rgba(37, 71, 241, 0.04);
  }

  &__check {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;

    ::v-deep .van-checkbox__icon {
      font-size: 34px;
      line-height: 34px;
      overflow: visible;
    }

    ::v-deep .van-checkbox__icon .van-icon {
      border-color: #6B7280;
      overflow: visible;
    }

    ::v-deep .van-checkbox__icon--checked .van-icon {
      border-color: var(--primary-color);
      background-color: var(--primary-color);
    }
  }

  &__body {
    min-width: 0;
    flex: 1;
    margin-left: 18px;
  }

  &__name {
    color: #0B1744;
    font-size: 28px;
    line-height: 38px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__bottom {
    display: flex;
    align-items: center;
    margin-top: 18px;
  }

  &__phone {
    flex: 1;
    min-width: 0;
    color: #0B1744;
    font-size: 24px;
    line-height: 34px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    margin-left: 20px;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    border: 0;
    padding: 0 10px;
    background: transparent;
    font-size: 24px;
    line-height: 34px;

    .van-icon {
      margin-right: 8px;
      font-size: 26px;
    }

    &--edit {
      color: var(--primary-color);
    }

    &--delete {
      color: var(--red-color);
    }
  }

  &__action-divider {
    width: 1px;
    height: 28px;
    background: #E5E7EB;
  }
}
</style>
